import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Eye, Shield, Brain, CheckCircle, Lock, FileCheck, Settings, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import CanonicalTag from "@/components/seo/CanonicalTag";

const ISHWAR = () => {
  useEffect(() => {
    document.title = "ISHWAR - The Guardian Within | Spharaka Networks";
    
    const metaDescription = document.querySelector('meta[name="description"]') || document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    metaDescription.setAttribute('content', 'ISHWAR: Intelligent Surveillance and Holistic Watchdog for Agentic Response. Spharaka\'s proprietary governance framework ensuring safe, compliant autonomous AI security.');
    if (!document.querySelector('meta[name="description"]')) {
      document.head.appendChild(metaDescription);
    }

    window.scrollTo(0, 0);

    return () => {
      try {
        if (metaDescription.parentNode) {
          document.head.removeChild(metaDescription);
        }
      } catch (e) {}
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <CanonicalTag path="/products/ishwar" />
      <Navbar />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[#8932c8]/5 via-white to-blue-500/5 py-20 md:py-32">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-[#8932c8] text-white hover:bg-[#8932c8]/90">
                <Eye className="w-4 h-4 mr-2" />
                The Guardian Within
              </Badge>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                <span className="bg-gradient-to-r from-[#8932c8] via-purple-600 to-[#8932c8] bg-clip-text text-transparent">
                  ISHWAR
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl font-semibold text-gray-800 mb-4">
                Intelligent Surveillance and Holistic Watchdog for Agentic Response
              </p>
              
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                At the heart of AuraXP™ lies ISHWAR - Spharaka's proprietary governance and control framework that ensures every autonomous decision made by AuraXP remains safe, compliant, and aligned with your organization's security mandates.
              </p>

              <p className="text-base text-gray-600 mb-10 leading-relaxed">
                As AuraXP evolves into an autonomous defense system capable of investigating, correlating, and even responding on its own, ISHWAR serves as the guardian layer that keeps its power in check. It provides real-time oversight, policy enforcement, and decision validation, ensuring that no autonomous action ever compromises trust or compliance.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="bg-[#8932c8] hover:bg-[#8932c8]/90 text-white font-semibold px-8 py-6 text-lg"
                  onClick={() => window.open('https://outlook.office.com/bookwithme/user/3c14c6b9047b45dfb64e4bc9045f29dd@spharaka.com/meetingtype/p0-SnGKD_EqEPlSvi_wdNg2?anonymous&ismsaljsauthenabled&ep=mlink', '_blank')}
                >
                  Request Demo
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-[#8932c8] text-[#8932c8] hover:bg-[#8932c8]/10 font-semibold px-8 py-6 text-lg"
                  asChild
                >
                  <Link to="/products">View All Products</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Purpose Section */}
        <section className="py-16 md:py-20 bg-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 bg-[#8932c8] text-white">Mission-Critical Governance</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Purpose
              </h2>
              <div className="bg-gradient-to-r from-[#8932c8]/10 to-blue-500/10 rounded-xl p-6 md:p-8">
                <p className="text-lg text-gray-800 leading-relaxed">
                  ISHWAR was built with one mission: <strong>to make autonomous AI security reliable and accountable</strong>. It ensures AuraXP never goes rogue, hallucinates, or takes actions beyond defined policies. Every AI-driven response passes through ISHWAR's governance logic before execution, safeguarding customer environments with precision and transparency.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Core Capabilities */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-[#8932c8] text-white">Advanced Control Systems</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Core Capabilities
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
              <div className="rounded-xl bg-white border-2 border-transparent hover:border-[#8932c8]/30 transition-all p-6 shadow-sm hover:shadow-md">
                <div className="w-14 h-14 bg-gradient-to-br from-[#8932c8] to-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-gray-800">Policy Enforcement</h3>
                <p className="text-gray-600 leading-relaxed">
                  ISHWAR enforces governance across all AI-driven actions. Every autonomous decision is checked against enterprise policies, access controls, and compliance frameworks to ensure no deviation from approved standards.
                </p>
              </div>

              <div className="rounded-xl bg-white border-2 border-transparent hover:border-blue-500/30 transition-all p-6 shadow-sm hover:shadow-md">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-400 rounded-xl flex items-center justify-center mb-4">
                  <Brain className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-gray-800">Decision Validation</h3>
                <p className="text-gray-600 leading-relaxed">
                  Before AuraXP executes a command or response, ISHWAR validates it for contextual accuracy, intent, and safety. This prevents unintended actions and maintains the predictability of AI-driven defense mechanisms.
                </p>
              </div>

              <div className="rounded-xl bg-white border-2 border-transparent hover:border-green-500/30 transition-all p-6 shadow-sm hover:shadow-md">
                <div className="w-14 h-14 bg-gradient-to-br from-green-600 to-green-400 rounded-xl flex items-center justify-center mb-4">
                  <Activity className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-gray-800">Continuous Monitoring</h3>
                <p className="text-gray-600 leading-relaxed">
                  Provides real-time oversight of all AuraXP activities through comprehensive audit trails and behavioral analytics. Administrators can review, approve, or rollback actions, maintaining complete transparency in autonomous operations.
                </p>
              </div>

              <div className="rounded-xl bg-white border-2 border-transparent hover:border-orange-500/30 transition-all p-6 shadow-sm hover:shadow-md">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-600 to-orange-400 rounded-xl flex items-center justify-center mb-4">
                  <Settings className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-gray-800">Supervised and Unsupervised Modes</h3>
                <p className="text-gray-600 leading-relaxed">
                  ISHWAR gives Spharaka admins total control to configure the AI's autonomy level. Switch seamlessly between Supervised Mode (manual validation before action) and Unsupervised Mode (full autonomous response) depending on situational requirements, risk level, or organizational policy.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why ISHWAR */}
        <section className="py-16 md:py-20 bg-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <Badge className="mb-4 bg-[#8932c8] text-white">Enterprise Benefits</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Why ISHWAR
                </h2>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex gap-4 p-6 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200">
                  <CheckCircle className="w-6 h-6 text-[#8932c8] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-purple-800">Trustworthy AI</h3>
                    <p className="text-gray-700">
                      Ensures all autonomous decisions are explainable, verifiable, and policy-aligned.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 p-6 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200">
                  <CheckCircle className="w-6 h-6 text-[#8932c8] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-purple-800">Compliance Ready</h3>
                    <p className="text-gray-700">
                      Built-in audit trails and regulatory mapping make AI operations compliance-ready from day one.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 p-6 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200">
                  <CheckCircle className="w-6 h-6 text-[#8932c8] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-purple-800">Risk Mitigation</h3>
                    <p className="text-gray-700">
                      Prevents AI hallucinations, overreach, or self-triggered responses that could endanger production systems.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 p-6 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200">
                  <CheckCircle className="w-6 h-6 text-[#8932c8] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-purple-800">Transparent Operations</h3>
                    <p className="text-gray-700">
                      Gives complete visibility into AuraXP's decision logic and action flow, ensuring continuous control and accountability.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Guardian Layer */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-purple-50">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 bg-[#8932c8] text-white">Next-Generation Security</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-800">
                The Guardian Layer for Autonomous Cyber Defense
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                In an age where AI agents can think, learn, and act, ISHWAR ensures they do so responsibly. It is not just a safeguard but the conscious layer of Spharaka's autonomous intelligence framework.
              </p>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#8932c8]/10 via-white to-blue-500/5">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center space-y-8">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
                Experience Responsible Autonomy
              </h2>
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Button 
                  size="lg" 
                  className="bg-[#8932c8] hover:bg-[#8932c8]/90 text-white font-semibold px-8 py-6 text-lg"
                  onClick={() => window.open('https://outlook.office.com/bookwithme/user/3c14c6b9047b45dfb64e4bc9045f29dd@spharaka.com/meetingtype/p0-SnGKD_EqEPlSvi_wdNg2?anonymous&ismsaljsauthenabled&ep=mlink', '_blank')}
                >
                  Request Demo
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-[#8932c8] text-[#8932c8] hover:bg-[#8932c8]/10 font-semibold px-8 py-6 text-lg"
                  asChild
                >
                  <Link to="/products">View All Products</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ISHWAR;
